import React, { FC, useEffect, useState } from 'react';
import { IonContent, IonPage, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import {
  Call,
  CallingState,
  CancelCallButton,
  OwnCapability,
  Restricted,
  ScreenShareButton,
  SpeakerLayout,
  SpeakingWhileMutedNotification,
  StreamCall,
  StreamTheme,
  StreamVideo,
  StreamVideoClient,
  ToggleAudioPublishingButton,
  ToggleVideoPublishingButton,
  useCallStateHooks,
  User,
  DefaultParticipantViewUI,
  useParticipantViewContext,
  useI18n,
  useMenuContext,
  GenericMenu,
  GenericMenuButtonItem,
  useCall,
  NoiseCancellationSettingsModeEnum,
  ToggleAudioOutputButton,
} from '@stream-io/video-react-sdk';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Loading } from '@mindly/ui-components';

import { getConsultation } from './utils';
import { notifyAboutJoinToTheVideoChat } from '../../api/notifications';
import { userSelector } from 'src/redux/user/selector';
import { getVideoCallToken } from 'src/api/videoCall';
import { environment } from 'src/enviroments';
import { PrivateRoutes } from 'src/routes/navigation';

import '@stream-io/video-react-sdk/dist/css/styles.css';
import './video-call.css';

type VideoCallPageParams = RouteComponentProps<{
  consultationID: string;
  psychologistID: string;
}>;

const NewVideoCall: React.FC<VideoCallPageParams> = ({ match, history }) => {
  const { consultationID } = match.params;

  const [client, setClient] = useState<StreamVideoClient>();
  const [call, setCall] = useState<Call>();
  const [isPresent, setIsPresent] = useState(false);

  const currentUser = useSelector(userSelector);
  const [isLoading, setIsLoading] = useState(true);

  useIonViewDidEnter(async () => {
    setIsPresent(true);
    try {
      setIsLoading(true);
      const consultationData = await getConsultation(consultationID);
      const tokenData = await getVideoCallToken(consultationID);
      const apiKey = environment.STREAM_API_TOKEN;
      const token = tokenData.data;
      const userId = currentUser?.id ?? '';
      const callId = consultationID;

      const user: User = {
        id: userId,
        name: `${currentUser?.display_name} ${currentUser?.last_name}`,
        image: currentUser?.avatar ?? '',
      };

      const clientInstance = new StreamVideoClient({ apiKey, user, token });
      const callInstance = clientInstance.call('default', callId);

      setClient(clientInstance);
      setCall(callInstance);
      await notifyAboutJoinToTheVideoChat(consultationData.user_id, consultationData.psychologist_id);
    } catch (error) {
      console.error('[StreamVideoCallCapacitor] web error', error);
    } finally {
      setIsLoading(false);
    }
  }, [consultationID]);

  useIonViewDidLeave(async () => {
    setIsPresent(false);
    setCall(undefined);
    setClient(undefined);
  });

  const onEnd = () => {
    history.push(PrivateRoutes.CONSULTATION);
  };

  return (
    <IonPage>
      <IonContent>
        <div className="flex items-center justify-center w-full h-screen bg-color-black">
          {isPresent && (
            <>
              <Loading isOpen={isLoading} />
              {client && call && (
                <StreamVideo client={client}>
                  <StreamCall call={call}>
                    <MyUILayout onEnd={onEnd} />
                  </StreamCall>
                </StreamVideo>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export const MyUILayout: FC<{ onEnd: () => void }> = ({ onEnd }) => {
  const call = useCall();

  const { useCallCallingState, useCameraState, useScreenShareState, useMicrophoneState } = useCallStateHooks();
  const callingState = useCallCallingState();

  const { status: cameraStatus } = useCameraState();
  const { status: micStatus } = useMicrophoneState();
  const { status: screenSnareStatus } = useScreenShareState();

  useEffect(() => {
    (async () => {
      await call?.join({
        create: true,
        data: {
          settings_override: {
            audio: {
              default_device: 'speaker',
              access_request_enabled: true,
              mic_default_on: true,
              speaker_default_on: true,
              noise_cancellation: { mode: NoiseCancellationSettingsModeEnum.DISABLED },
            },
            video: {
              access_request_enabled: true,
              camera_default_on: true,
              camera_facing: 'front',
              enabled: true,
              target_resolution: {
                width: 1920,
                height: 1080,
              },
            },
            screensharing: {
              access_request_enabled: true,
              enabled: true,
              target_resolution: {
                width: 1920,
                height: 1080,
              },
            },
          },
        },
      });
    })();

    return () => {
      onLeave();
    };
  }, []);

  const disableCamera = async () => {
    if (cameraStatus === 'enabled') {
      await call?.muteSelf('video');
    }
  };

  const disableMic = async () => {
    if (micStatus === 'enabled') {
      await call?.muteSelf('audio');
    }
  };

  const disableScreenshare = async () => {
    if (screenSnareStatus === 'enabled') {
      await call?.muteSelf('screenshare');
    }
  };

  const onLeave = async () => {
    if (!call) return;

    if (callingState === CallingState.LEFT) return;

    try {
      await disableCamera();
      await disableScreenshare();
      await disableMic();

      await call.leave();
    } catch (e) {
      console.error(e);
    } finally {
      onEnd();
    }
  };

  if (callingState !== CallingState.JOINED) {
    return null;
  }

  return (
    <StreamTheme>
      <SpeakerLayout
        participantsBarPosition="bottom"
        ParticipantViewUISpotlight={
          <DefaultParticipantViewUI ParticipantActionsContextMenu={ParticipantActionsContextMenu} />
        }
        ParticipantViewUIBar={
          <DefaultParticipantViewUI ParticipantActionsContextMenu={ParticipantActionsContextMenu} />
        }
      />
      <div className="str-video__call-controls">
        <Restricted requiredGrants={[OwnCapability.SEND_AUDIO]}>
          <SpeakingWhileMutedNotification>
            <ToggleAudioPublishingButton />
          </SpeakingWhileMutedNotification>
        </Restricted>
        <ToggleAudioOutputButton />
        <Restricted requiredGrants={[OwnCapability.SEND_VIDEO]}>
          <ToggleVideoPublishingButton />
        </Restricted>
        <Restricted requiredGrants={[OwnCapability.SCREENSHARE]}>
          <ScreenShareButton />
        </Restricted>
        <CancelCallButton onClick={onLeave} />
      </div>
    </StreamTheme>
  );
};

export default NewVideoCall;

const ParticipantActionsContextMenu: FC = () => {
  const { participantViewElement } = useParticipantViewContext();
  const [fullscreenModeOn, setFullscreenModeOn] = useState(!!document.fullscreenElement);
  const { t } = useI18n();

  const toggleFullscreenMode = () => {
    if (!fullscreenModeOn) {
      return participantViewElement?.requestFullscreen().catch(console.error);
    }
    return document.exitFullscreen().catch(console.error);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreenModeOn(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const { close } = useMenuContext() || {};
  return (
    <GenericMenu onItemClick={close}>
      {participantViewElement && (
        <GenericMenuButtonItem onClick={toggleFullscreenMode}>
          {t('{{ direction }} fullscreen', {
            direction: fullscreenModeOn ? t('Leave') : t('Enter'),
          })}
        </GenericMenuButtonItem>
      )}
    </GenericMenu>
  );
};
